import axios from "axios";
import { jwtDecode } from 'jwt-decode';
import { SERVER_API } from '../config/config';
import { getAccessToken, setAccessToken, setAuth, setStatus, clearAuth } from '../stores/authStore';

// No instance-level withCredentials: data endpoints authenticate via the Bearer
// header only. Credentials are sent per-request solely where the httpOnly
// refreshToken cookie is involved (verify-otp, refresh, logout), so wildcard
// CORS on the backend can never break ordinary API calls.
const api = axios.create({
  baseURL: `${SERVER_API}`,
  headers: {
    "Content-Type": "application/json",
    "requestkey": process.env?.REACT_APP_REQUEST_KEY,
  },
});

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers["Authorization"] = 'Bearer ' + token;
    }
    config.headers["requestkey"] = process.env?.REACT_APP_REQUEST_KEY;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// userType must be read before clearAuth() removes it from localStorage
export const redirectToLogin = () => {
  const userType = localStorage.getItem('userType');
  clearAuth();
  window.location.href = userType === 'owner' ? '/partner' : '/signin';
};

/**
 * Single-flight refresh token operation.
 * Can be called directly (e.g. on app startup or by AuthGuard) or via 401 interceptor.
 */
export const refreshAccessToken = async () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  setStatus('Refreshing');

  try {
    // Refresh token travels only in the httpOnly cookie, never the body (auth.md §1, §4)
    const { data } = await axios.post(
      `${SERVER_API}/users/refresh-token`,
      {},
      {
        withCredentials: true,
        headers: {
          "requestkey": process.env?.REACT_APP_REQUEST_KEY,
        },
      }
    );

    const newToken = data.accessToken || data.token || data.data?.accessToken || data.data?.token;

    if ((data.status || data.success) && newToken) {
      setAccessToken(newToken);

      // Hydrate user info & role in authStore if decoded token has payload
      try {
        const decoded = jwtDecode(newToken);
        const user = decoded.user || decoded.admin || decoded.owner || {};
        const role = decoded.role || user.role || (decoded.admin ? 'admin' : decoded.owner ? 'partner' : null);
        setAuth(user, newToken, role);
      } catch {
        // Ignore decode error fallback
      }

      processQueue(null, newToken);
      return newToken;
    }

    throw new Error(data.message || 'Refresh failed');
  } catch (refreshError) {
    processQueue(refreshError, null);
    clearAuth();
    throw refreshError;
  } finally {
    isRefreshing = false;
  }
};

const isJwtExpiredError = (data) => {
  if (!data || typeof data !== 'object') return false;
  if (data.statusCode === 401 || data.statusCode === 403) return true;

  if (data.status === false) {
    const code = String(data.code || '').toUpperCase();
    const msg = String(data.message || '').toLowerCase();

    // Specific auth token error codes
    if (
      code === 'INVALID_ACCESS_TOKEN' ||
      code === 'TOKEN_EXPIRED' ||
      code === 'EXPIRED_ACCESS_TOKEN' ||
      code === 'UNAUTHORIZED' ||
      code === 'INVALID_TOKEN'
    ) {
      return true;
    }

    // Specific auth token error messages
    if (
      msg.includes('jwt expired') ||
      msg.includes('jwt malformed') ||
      msg.includes('invalid token') ||
      msg.includes('token expired') ||
      msg.includes('verify your session')
    ) {
      return true;
    }
  }

  return false;
};

api.interceptors.response.use(
  async (response) => {
    const originalRequest = response.config;

    // Backend returns payload indicating JWT / access token error
    if (response.data && isJwtExpiredError(response.data)) {
      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const newAccessToken = await refreshAccessToken();
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          if (originalRequest.headers["authorization"]) {
            originalRequest.headers["authorization"] = `Bearer ${newAccessToken}`;
          }
          return api(originalRequest);
        } catch (refreshError) {
          redirectToLogin();
          return Promise.reject(refreshError);
        }
      }
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Backend returns HTTP 401 or 403 error status code
    if ((error.response?.status === 401 || error.response?.status === 403 || isJwtExpiredError(error.response?.data)) && originalRequest) {
      if (!originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const newAccessToken = await refreshAccessToken();
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          if (originalRequest.headers["authorization"]) {
            originalRequest.headers["authorization"] = `Bearer ${newAccessToken}`;
          }
          return api(originalRequest);
        } catch (refreshError) {
          redirectToLogin();
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;

